/**
 * © 2025 Indikin.online. All rights reserved.
 * Made with ❤️ by YalaCoder.
 *
 * Modal Loading Component
 *
 * --- SUMMARY (V2 - Linter Fix) ---
 * - FIX: Removed the unnecessary `import React from 'react';` statement.
 * - REASON: With modern JSX transforms (used by Vite), React no longer
 * needs to be in scope to use JSX syntax. This change resolves the
 * `no-unused-vars` linting error.
 *
 * --- SUMMARY (V1 - Initial Implementation) ---
 * - NEW COMPONENT: This is a brand-new component created to serve as the
 * Suspense fallback when a lazy-loaded modal is being fetched.
 * - PURPOSE: It provides an immediate visual cue to the user that their
 * action has been registered, bridging the brief gap during code-splitting.
 * - UI: The component renders a full-screen, semi-transparent, blurred
 * backdrop, which is visually consistent with the actual `Modal.jsx`
 * component. A centered spinner with a custom `fade-in-fast` animation
 * is displayed on top of the backdrop.
 * - This ensures a seamless and professional-looking transition from the
 * moment a user clicks to the moment the modal content appears.
 */

const ModalLoading = () => {
  return (
    // This div creates the full-screen backdrop.
    // - `fixed inset-0`: Stretches the element to cover the entire viewport.
    // - `z-[9999]`: Ensures it appears above all other page content.
    // - `bg-black/75`: A semi-transparent black background.
    // - `backdrop-blur-sm`: Applies a blur effect to the content behind it.
    // - `flex items-center justify-center`: Centers its child (the spinner).
    // - `animate-fade-in`: A gentle fade-in for the backdrop itself.
    <div
      className="fixed inset-0 z-[9999] bg-black/75 backdrop-blur-sm flex items-center justify-center animate-fade-in"
      role="status"
      aria-live="polite"
    >
      {/* This is the visual spinner element. */}
      {/* - `animate-spin`: A standard Tailwind utility for rotation. */}
      {/* - `rounded-full`: Makes it a circle. */}
      {/* - `h-16 w-16`: Defines its size. */}
      {/* - `border-t-2 border-b-2 border-primary`: Creates the classic spinner look with a colored top/bottom border. */}
      {/* - `animate-fade-in-fast`: A custom, quicker fade-in applied specifically to the spinner for a snappy feel. */}
      <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary animate-fade-in-fast"></div>
      <span className="sr-only">Loading...</span>
    </div>
  );
};

export default ModalLoading;
